import { motion } from "framer-motion";

// ======= PAGE TRANSITIONS =======
export const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -20,
  },
};

export const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.5,
};

export const PageAnimation = ({ children, className = "" }) => (
  <motion.div
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
    className={className}
  >
    {children}
  </motion.div>
);

// ======= STAGGERED CHILDREN =======
export const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.1,
    },
  },
};

export const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

export const StaggerContainer = ({ children, className = "" }) => (
  <motion.div
    variants={containerVariants}
    initial="hidden"
    animate="visible"
    className={className}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "" }) => (
  <motion.div variants={itemVariants} className={className}>
    {children}
  </motion.div>
);

// ======= FADE ANIMATIONS =======
export const fadeInUp = {
  initial: {
    y: 60,
    opacity: 0,
  },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

export const fadeInDown = {
  initial: {
    y: -60,
    opacity: 0,
  },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

export const fadeInLeft = {
  initial: {
    x: -60,
    opacity: 0,
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

export const fadeInRight = {
  initial: {
    x: 60,
    opacity: 0,
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

export const fadeInScale = {
  initial: {
    scale: 0.9,
    opacity: 0,
  },
  animate: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

// ======= COMPONENT ANIMATIONS =======
export const cardHover = {
  initial: {
    scale: 1,
    y: 0,
  },
  hover: {
    scale: 1.02,
    y: -5,
    transition: {
      duration: 0.3,
      ease: "easeInOut",
    },
  },
};

export const buttonHover = {
  initial: {
    scale: 1,
  },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.2,
      ease: "easeInOut",
    },
  },
  tap: {
    scale: 0.95,
  },
};

export const iconHover = {
  initial: {
    scale: 1,
    rotate: 0,
  },
  hover: {
    scale: 1.1,
    rotate: 5,
    transition: {
      duration: 0.2,
      ease: "easeInOut",
    },
  },
};

export const navItemHover = {
  initial: {
    scale: 1,
  },
  hover: {
    scale: 1.05,
    color: "var(--primary-500)",
    transition: {
      duration: 0.2,
    },
  },
};

// ======= LOADING ANIMATIONS =======
export const pulseAnimation = {
  initial: {
    scale: 1,
    opacity: 1,
  },
  animate: {
    scale: [1, 1.05, 1],
    opacity: [1, 0.7, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

export const shimmerAnimation = {
  initial: {
    x: "-100%",
  },
  animate: {
    x: "100%",
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

export const spinnerRotation = {
  animate: {
    rotate: 360,
    transition: {
      duration: 1,
      repeat: Infinity,
      ease: "linear",
    },
  },
};

// ======= SEARCH ANIMATIONS =======
export const searchOverlayVariants = {
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: "easeInOut",
    },
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

export const searchContentVariants = {
  hidden: {
    scale: 0.9,
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.3,
    },
  },
  visible: {
    scale: 1,
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

// ======= SPECIAL EFFECTS =======
export const floatingAnimation = {
  initial: {
    y: 0,
  },
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

export const bounceIn = {
  initial: {
    scale: 0.3,
    opacity: 0,
  },
  animate: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 15,
    },
  },
};

export const slideInFromBottom = {
  initial: {
    y: 100,
    opacity: 0,
  },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

// ======= NOTIFICATION ANIMATIONS =======
export const toastEnter = {
  initial: {
    x: 300,
    opacity: 0,
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20,
    },
  },
};

export const toastExit = {
  exit: {
    x: 300,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: "easeIn",
    },
  },
};

// ======= ANIMATION COMPONENTS =======

// Animated Card Component
export const AnimatedCard = ({
  children,
  className = "",
  whileHover = true,
}) => (
  <motion.div
    className={className}
    initial="initial"
    whileInView="animate"
    whileHover={whileHover ? "hover" : undefined}
    viewport={{ once: true, margin: "-50px" }}
    variants={{
      initial: fadeInScale.initial,
      animate: fadeInScale.animate,
      hover: cardHover.hover,
    }}
  >
    {children}
  </motion.div>
);

// Animated Button Component
export const AnimatedButton = ({ children, className = "", ...props }) => (
  <motion.button
    className={className}
    whileHover="hover"
    whileTap="tap"
    variants={buttonHover}
    {...props}
  >
    {children}
  </motion.button>
);

// Animated Icon Component
export const AnimatedIcon = ({ children, className = "" }) => (
  <motion.div className={className} whileHover="hover" variants={iconHover}>
    {children}
  </motion.div>
);

// Loading Spinner Component
export const LoadingSpinner = ({ size = "medium", className = "" }) => {
  const sizes = {
    small: "w-4 h-4",
    medium: "w-8 h-8",
    large: "w-12 h-12",
  };

  return (
    <motion.div
      className={`border-2 border-primary-200 border-t-primary-600 rounded-full ${sizes[size]} ${className}`}
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
    />
  );
};

// Shimmer Loading Component
export const ShimmerLoader = ({ className = "" }) => (
  <motion.div
    className={`relative overflow-hidden bg-gray-200 dark:bg-gray-700 ${className}`}
    initial="initial"
    animate="animate"
    variants={shimmerAnimation}
  >
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent"
      variants={shimmerAnimation}
    />
  </motion.div>
);

// Scroll Progress Indicator
export const ScrollProgress = () => (
  <motion.div
    className="fixed top-0 left-0 right-0 h-1 bg-primary-500 z-50"
    style={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    viewport={{ once: false }}
    transition={{ duration: 0.5 }}
  />
);

// ======= ANIMATION HOOKS =======
import { useInView } from "framer-motion";
import { useState, useRef } from "react";

export const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: threshold });

  return { ref, isInView };
};

export const useHoverAnimation = () => {
  const [isHovered, setIsHovered] = useState(false);

  const hoverProps = {
    onHoverStart: () => setIsHovered(true),
    onHoverEnd: () => setIsHovered(false),
  };

  return { isHovered, hoverProps };
};

// ======= ANIMATION PRESETS =======
export const animationPresets = {
  // Page Sections
  heroSection: {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" },
    viewport: { once: true, margin: "-100px" },
  },

  featureCard: {
    initial: { opacity: 0, scale: 0.9 },
    whileInView: { opacity: 1, scale: 1 },
    whileHover: { scale: 1.02, y: -5 },
    transition: { duration: 0.5, ease: "easeOut" },
    viewport: { once: true },
  },

  productGrid: {
    initial: { opacity: 0 },
    whileInView: { opacity: 1 },
    transition: { staggerChildren: 0.1 },
    viewport: { once: true },
  },

  // Navigation
  navLink: {
    whileHover: { scale: 1.05, color: "var(--primary-500)" },
    whileTap: { scale: 0.95 },
  },

  // Interactive Elements
  interactiveCard: {
    whileHover: {
      scale: 1.02,
      y: -8,
      transition: { type: "spring", stiffness: 300 },
    },
    whileTap: { scale: 0.98 },
  },
};

// ======= EXPORT ALL ANIMATIONS =======
export default {
  // Page Transitions
  pageVariants,
  pageTransition,
  PageAnimation,

  // Staggered Animations
  containerVariants,
  itemVariants,
  StaggerContainer,
  StaggerItem,

  // Fade Animations
  fadeInUp,
  fadeInDown,
  fadeInLeft,
  fadeInRight,
  fadeInScale,

  // Component Animations
  cardHover,
  buttonHover,
  iconHover,
  navItemHover,

  // Loading Animations
  pulseAnimation,
  shimmerAnimation,
  spinnerRotation,

  // Search Animations
  searchOverlayVariants,
  searchContentVariants,

  // Special Effects
  floatingAnimation,
  bounceIn,
  slideInFromBottom,

  // Notification Animations
  toastEnter,
  toastExit,

  // Reusable Components
  AnimatedCard,
  AnimatedButton,
  AnimatedIcon,
  LoadingSpinner,
  ShimmerLoader,
  ScrollProgress,

  // Hooks
  useScrollAnimation,
  useHoverAnimation,

  // Presets
  animationPresets,
};
